import React, { useState, useEffect } from 'react';
import { AlertCircle, Save, X } from 'lucide-react';
import { Testimonial, TestimonialInput, isTitleValid, isContentValid } from '../lib/testimonials';

interface TestimonialFormProps {
  initialData?: Testimonial;
  onSubmit: (data: TestimonialInput) => Promise<void>;
  onCancel: () => void;
}

export default function TestimonialForm({ initialData, onSubmit, onCancel }: TestimonialFormProps) {
  const [formData, setFormData] = useState<TestimonialInput>({
    title: '',
    content: ''
  });
  
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  
  useEffect(() => {
    if (initialData) {
      setFormData({
        title: initialData.title,
        content: initialData.content
      });
    }
  }, [initialData]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Limpar erro do campo quando o usuário digita
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.title.trim()) {
      newErrors.title = 'O título é obrigatório';
    } else if (!isTitleValid(formData.title)) {
      newErrors.title = 'O título deve ter no máximo 100 caracteres';
    }
    
    if (!formData.content.trim()) {
      newErrors.content = 'O relato é obrigatório';
    } else if (!isContentValid(formData.content)) {
      newErrors.content = 'O relato deve ter no máximo 2000 caracteres';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);
    try {
      await onSubmit(formData);
    } catch (error) {
      console.error('Error submitting form:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm p-6">
      <div className="flex justify-between items-start mb-6">
        <h2 className="text-xl font-semibold text-brand-teal">
          {initialData ? 'Editar Testemunho' : 'Novo Testemunho'}
        </h2>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="space-y-6">
          {/* Título */}
          <div>
            <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
              Título * <span className="text-gray-500">({formData.title.length}/100 caracteres)</span>
            </label>
            <input
              type="text"
              id="title"
              name="title"
              value={formData.title}
              onChange={handleChange}
              className={`w-full px-4 py-2 border ${
                errors.title ? 'border-red-500' : 'border-gray-300'
              } rounded-lg focus:ring-2 focus:ring-brand-teal focus:border-brand-teal`}
              placeholder="Título do seu testemunho"
              maxLength={100}
            />
            {errors.title && (
              <p className="mt-1 text-sm text-red-500">{errors.title}</p>
            )}
          </div>
          
          {/* Conteúdo */}
          <div>
            <label htmlFor="content" className="block text-sm font-medium text-gray-700 mb-1">
              Relato * <span className="text-gray-500">({formData.content.length}/2000 caracteres)</span>
            </label>
            <textarea
              id="content"
              name="content"
              value={formData.content}
              onChange={handleChange}
              rows={8}
              className={`w-full px-4 py-2 border ${
                errors.content ? 'border-red-500' : 'border-gray-300'
              } rounded-lg focus:ring-2 focus:ring-brand-teal focus:border-brand-teal`}
              placeholder="Compartilhe seu testemunho em detalhes..."
              maxLength={2000}
            />
            {errors.content && (
              <p className="mt-1 text-sm text-red-500">{errors.content}</p>
            )}
          </div>
        </div>
        
        {Object.keys(errors).length > 0 && (
          <div className="mt-4 p-4 bg-red-50 text-red-600 rounded-lg flex items-start">
            <AlertCircle className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium">Corrija os erros abaixo:</p>
              <ul className="list-disc list-inside text-sm mt-1">
                {Object.values(errors).map((error, index) => (
                  <li key={index}>{error}</li>
                ))}
              </ul>
            </div>
          </div>
        )}
        
        <div className="mt-6 flex justify-end gap-4">
          <button
            type="button"
            onClick={onCancel}
            className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={loading}
            className="bg-brand-teal text-white px-6 py-2 rounded-lg hover:bg-brand-teal/90 transition-colors disabled:opacity-50 flex items-center gap-2"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                <span>Enviando...</span>
              </>
            ) : (
              <>
                <Save className="h-4 w-4" />
                <span>Enviar Testemunho</span>
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}